<template>
  <div class="setting-form">
    <div class="setting-form-header">
      <drawer-dialog-controls
        :is-loading="isLoading"
        :is-disabled="isDisabledForm"
        @close="$emit('close')"
        @save="addNewNotification"
      >
        <slot />
      </drawer-dialog-controls>
    </div>

    <div class="setting-form-content">
      <v-row class="mx-0">
        <v-text-field
          v-model="settingData.name"
          hide-details="auto"
          label="Name"
          style="min-width: 220px; max-width: 220px"
        ></v-text-field>
        <v-select
          v-model="settingData.rules.type"
          :items="NOTIFICATION_TYPES"
          class="ml-10"
          hide-details
          attach
          label="Type"
          style="min-width: 220px; max-width: 220px"
        />
      </v-row>
      <div class="setting-form-content__body">
        <v-card
          class="my-4 py-4 setting-form-content__body-card"
          elevation="3"
        >
          <v-row
            class="mx-0 px-4"
          >
            <div class="setting-form-content__body-title">
              <span>Notifications settings</span>
            </div>
          </v-row>
          <v-divider class="mt-3"></v-divider>

          <v-select
            v-model="settingData.trigger"
            :items="triggers"
            class="ml-4 mt-3"
            hide-details
            attach
            label="Trigger"
            style="min-width: 220px; max-width: 220px"
          />
          <div
            v-if="hasCustomFilter || customFilterOptions.length"
            class="ml-4 mt-3"
          >
            <v-label>
              Custom Filter:
              <v-menu
                v-if="unusedFilterOptions.length"
              >
                <template #activator="{on, attrs}">
                  <v-btn
                    color="primary"
                    icon
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>
                      mdi-plus-circle-outline
                    </v-icon>
                  </v-btn>
                </template>

                <v-list>
                  <v-list-item
                    v-for="filterOption in unusedFilterOptions"
                    :key="filterOption"
                    @click="onAddCustomFilter(filterOption)"
                  >
                    {{ filterOption }}
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-label>
            <v-row
              v-for="(_, key) in settingData.rules.custom_filter"
              :key="key"
              class="mx-0"
            >
              <v-col
                v-if="key === 'benefit_id' && hasBonusToolsAccess"
                cols="4"
                class="pl-0"
              >
                <v-select
                  :value="settingData.rules.custom_filter[key].value.split(',').map(Number)"
                  label="Benefit"
                  multiple
                  hide-details
                  :items="benefitOptions"
                  @input="settingData.rules.custom_filter[key].value = $event.join(',')"
                >
                  <template v-slot:item="{attrs, on, item}">
                    <v-list-item v-bind="attrs" v-on="on">
                      <v-list-item-action>
                        <v-simple-checkbox
                          :value="attrs.inputValue"
                        />
                      </v-list-item-action>

                      <v-list-item-content>
                        <v-list-item-title>
                          <span v-html="item.text"></span>
                          <small class="float-right">({{ item.value }})</small>
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
              <template v-else>
                <v-text-field
                  :value="key"
                  readonly
                  :hide-details="!settingData.rules.custom_filter[key].hint"
                  :persistent-hint="!!settingData.rules.custom_filter[key].hint"
                  :hint="settingData.rules.custom_filter[key].hint"
                  label="Field"
                  style="min-width: 200px; max-width: 200px"
                />
                <v-select
                  v-model="settingData.rules.custom_filter[key].equation"
                  :items="SEGMENT_EQUATION"
                  class="ml-4"
                  hide-details
                  attach
                  label="Equation"
                  :disabled="key === 'benefit_id' && !hasBonusToolsAccess"
                  style="min-width: 200px; max-width: 200px"
                />
                <v-text-field
                  v-model="settingData.rules.custom_filter[key].value"
                  class="ml-4"
                  hide-details
                  :disabled="key === 'benefit_id' && !hasBonusToolsAccess"
                  label="Value"
                  style="min-width: 200px; max-width: 200px"
                  />
                </template>
                <v-btn
                icon
                class="align-self-center ml-2"
                color="error"
                :disabled="key === 'benefit_id' && !hasBonusToolsAccess"
                @click="$delete(settingData.rules.custom_filter, key)"
              >
                <v-icon>
                  mdi-close-circle
                </v-icon>
              </v-btn>
            </v-row>
          </div>

          <v-divider class="mt-4" />
          <v-container>
            <v-label>Availability</v-label>

            <v-row class="mx-0 px-0">
              <v-select
                v-model="settingData.rules.availability.period"
                :items="AVAILABILITY_PERIODS"
                class="mr-4"
                hide-details
                attach
                style="min-width: 200px; max-width: 200px;"
                label="Type"
              />

              <template v-if="settingData?.rules?.availability?.period === 'period'">
                <v-text-field
                  v-model="settingData.rules.availability.start_date"
                  label="Date Start"
                  prepend-icon="event"
                  v-mask="`####-##-## ##:##:##`"
                  hint="Format: YYYY-MM-DD HH:mm:ss"
                  placeholder="YYYY-MM-DD HH:mm:ss"
                  persistent-placeholder
                  persistent-hint
                  style="min-width: 240px; max-width: 240px"
                />
                <v-text-field
                  v-model="settingData.rules.availability.end_date"
                  label="Date To"
                  prepend-icon="event"
                  v-mask="`####-##-## ##:##:##`"
                  hint="Format: YYYY-MM-DD HH:mm:ss"
                  placeholder="YYYY-MM-DD HH:mm:ss"
                  persistent-placeholder
                  persistent-hint
                  style="min-width: 240px; max-width: 240px"
                />
              </template>

              <template v-if="settingData?.rules?.availability?.period === 'monthday'">
                <v-select
                  v-model="settingData.rules.availability.days"
                  hide-details
                  label="Days"
                  chips
                  small-chips
                  clearable
                  multiple
                  attach
                  :items="Array.from({length: 31}, (_, i) => i + 1)"
                />
              </template>

              <template v-if="settingData?.rules?.availability?.period === 'weekday'">
                <v-select
                  v-model="settingData.rules.availability.days"
                  hide-details
                  label="Days"
                  chips
                  small-chips
                  clearable
                  multiple
                  attach
                  :items="WEEKDAYS"
                />
              </template>

              <template v-if="settingData?.rules?.availability?.period === 'daily'">
                <v-text-field
                  v-model="settingData.rules.availability.start_time"
                  label="Time Start"
                  key="start_time"
                  prepend-icon="event"
                  v-mask="`##:##:##`"
                  hint="Format: HH:mm:ss"
                  placeholder="HH:mm:ss"
                  persistent-placeholder
                  persistent-hint
                  style="min-width: 240px; max-width: 240px"
                />
                <v-text-field
                  v-model="settingData.rules.availability.end_time"
                  label="Time End"
                  key="end_time"
                  prepend-icon="event"
                  v-mask="`##:##:##`"
                  hint="Format: HH:mm:ss"
                  placeholder="HH:mm:ss"
                  persistent-placeholder
                  persistent-hint
                  style="min-width: 240px; max-width: 240px"
                />
              </template>
            </v-row>
          </v-container>

          <v-divider />

          <v-container>
            <v-label>Recurring</v-label>

            <v-row class="mx-0 px-0">
              <v-select
                v-model="settingData.rules.recurring.period"
                :items="RECURRING_PERIODS"
                class="mr-4"
                hide-details
                attach
                style="min-width: 200px; max-width: 200px;"
                label="Type"
              />

              <template v-if="settingData?.rules?.recurring?.period === 'custom'">
                <v-text-field
                  v-model="settingData.rules.recurring.amount"
                  label="Amount"
                  class="mr-3"
                  hide-details
                  style="min-width: 80px; max-width: 80px"
                />

                <v-select
                  v-model="settingData.rules.recurring.unit"
                  label="Units"
                  :items="RECURRING_UNITS"
                  class="mr-4"
                  hide-details
                  attach
                  style="min-width: 120px; max-width: 120px;"
                />
              </template>

            </v-row>
          </v-container>

          <v-divider class="mt-1" />

          <template v-if="hasMailTemplateAccess">
            <v-container>
              <v-label>
                Mail Template
              </v-label>

              <v-row class="mx-0 px-0">
                <v-select
                  v-model="settingData.rules.mail_template_id"
                  :items="mailTemplates"
                  item-text="name"
                  item-value="template_id"
                  attach
                  clearable
                  style="min-width: 120px; max-width: 620px;"
                />
              </v-row>
            </v-container>

            <v-divider class="mt-1" />
          </template>

          <v-container>
            <v-label>TTL</v-label>

            <v-row class="mx-0  px-0 pb-0 pt-4  setting-form-content__body-info">
              <v-select
                v-model="settingData.rules.ttl.type"
                :items="TTL"
                hide-details
                attach
                label="After Read Type"
                style="min-width: 150px; max-width: 150px"
              />
              <v-text-field
                v-model="settingData.rules.ttl.amount"
                label="After Read Amount"
                persistent-hint
                style="min-width: 150px; max-width: 150px"
              />
            </v-row>

            <v-row class="mx-0 pa-0 setting-form-content__body-info">
              <v-select
                v-model="settingData.rules.global_ttl.type"
                :items="TTL"
                hide-details
                attach
                label="Global Type"
                style="min-width: 150px; max-width: 150px"
              />
              <v-text-field
                v-model="settingData.rules.global_ttl.amount"
                label="Global Amount"
                persistent-hint
                style="min-width: 150px; max-width: 150px"
              />
            </v-row>
          </v-container>

        </v-card>

        <v-card
          class="my-4 py-4 setting-form-content__body-card"
          elevation="3"
        >
          <v-row
            class="mx-0 px-4"
          >
            <div class="setting-form-content__body-title">
              <span>Audience</span>
            </div>

          </v-row>
          <v-divider class="mt-3"></v-divider>
          <v-row class="mx-0 align-center">
            <v-combobox
              :value="accountConditionsIds"
              :search-input.sync="search"
              class="px-4 mt-4"
              clearable
              hide-selected
              attach
              label="Users ID"
              multiple
              persistent-hint
              small-chips
              counter
              style="min-width: 800px; max-width: 800px"
              @change="addUserId(search)"
            >
              <template v-slot:no-data>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>
                      Press <kbd>enter</kbd> to create a new one
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip
                  :input-value="selected"
                  close
                  v-bind="attrs"
                  @click="select"
                  @click:close="remove(item)"
                >
                  {{ item }}
                </v-chip>
              </template>
            </v-combobox>

            <v-btn
              v-text="'Upload'"
              color="primary"
              @click="$refs.import.value = null, $refs.import.click()"
            />
            <input
              ref="import"
              type="file"
              hidden
              accept=".csv"
              @change="onImportChange"
            >
          </v-row>
          <v-card class="ma-4 setting-form-content__body-filter" oulined>
            <div class="setting-form-content__body-filter-header">
              <div>
                <span>User segment filter</span>
              </div>
              <v-btn
                class="setting-form-content__body-btn"
                color="#27AE60"
                @click="addRule"
              >Add rules
              </v-btn>
            </div>

            <v-card v-for="(rule,idx) in filteredConditions" :key="idx" class="ma-4 setting-form-content__body-filter filter-size" oulined>
              <v-row class="mx-0 px-10 py-8 setting-form-content__body-filter-options">
                <v-select
                  v-model="rule.field"
                  :items="SEGMENT_FIELDS"
                  class="ml-4 mt-3"
                  hide-details
                  attach
                  :menu-props="{top: true}"
                  label="Field"
                  style="min-width: 200px; max-width: 200px"
                />
                <v-select
                  v-model="rule.equation"
                  :items="SEGMENT_EQUATION"
                  class="ml-4 mt-3"
                  hide-details
                  attach
                  :menu-props="{top: true}"
                  label="Equation"
                  style="min-width: 200px; max-width: 200px"
                />
                <v-text-field
                  v-model="rule.value"
                  class="ml-4 mt-3"
                  hide-details
                  label="Value"
                  style="min-width: 200px; max-width: 200px"
                />

              </v-row>
              <div class="setting-form-content__body-icon" @click="deleteRule(rule)">
                <v-icon>mdi-trash-can-outline</v-icon>
              </div>
            </v-card>
          </v-card>
        </v-card>

        <v-card
          class="my-4 py-4 setting-form-content__body-card"
          elevation="3"
        >
          <v-row
            class="mx-0 px-4"
            justify="space-between"
          >
            <div class="setting-form-content__body-title">
              <span>Content</span>
            </div>
          </v-row>
          <v-divider class="mt-3"></v-divider>
          <v-card class="ma-4 setting-form-content__body-localization" oulined>
            <v-row class="mx-0 pa-4 align-center setting-form-content__body-localization-content">
              <div class="setting-form-content__body-localization-text">
                <span>Localization</span>
              </div>
              <select-lang
                v-model="settingData.languages"
                :isOpen="isOpen"
                :isRounded="false"
                :languages="$store.state.auth.languages"
                class="setting-form-content__body-localization-select-lang"
                @addLang="addTranslationContent"
                @deleteLang="deleteTranslationContent"
                @open="(e) => toggleLanguages(e)"
              >
                <template v-slot:header>
                  <div class="setting-form-content__body-localization-select-lang-btn">
                    <v-icon>mdi-plus</v-icon>
                  </div>
                </template>
              </select-lang>
              <div class="d-flex mr-2 setting-form-content__body-localization-choice">
                <v-chip-group
                  active-class="setting-form-content__body-localization-choice--active"
                  :value="chosenTranslation"
                  column
                  @change="onChangeChosenTranslation"
                >
                  <div v-for="(lang, idx) in settingData.languages"
                       :key="lang"
                       @click="chooseLangForEdit(idx)"
                  >
                    <lang-chip
                      :color="'#6FCF97'"
                      :colorText="'white--text'"
                      :lang="{
                      locale: lang,
                      urlFlag: mapperIsoLangToOurLocal(localeToIsoPrefix(lang)),
                      }"
                    />
                  </div>
                </v-chip-group>
              </div>
            </v-row>
          </v-card>
          <!--          <v-card class="ma-4 setting-form-content__body-set" oulined>-->
          <div class="setting-form-content__body-set">
            <!--            <div class="setting-form-content__body-set-title">-->
            <!--              <span>Notification set</span>-->
            <!--            </div>-->
            <v-row class="mx-0 mb-5 setting-form-content__body-set-options">
              <v-checkbox
                v-model="selectedNotificationSet"
                hide-details
                label="List"
                value="base"
              />
              <v-checkbox
                v-model="selectedNotificationSet"
                hide-details
                label="Toast"
                value="toast"
              />
              <v-checkbox
                v-model="selectedNotificationSet"
                hide-details
                label="Pop-up"
                value="popup"
              />

              <v-tooltip
                v-if="!isSuggestionsDisplayed && currentTriggerVars.length"
                top
              >
                <template #activator="{on, attrs}">
                  <v-btn
                    icon
                    color="primary"
                    class="align-self-end ml-auto"
                    v-on="on"
                    v-bind="attrs"
                    @click="isSuggestionsDisplayed = true"
                  >
                    <v-icon>mdi-help-circle</v-icon>
                  </v-btn>
                </template>
                <span>Show dynamic variable options</span>
              </v-tooltip>
            </v-row>
          </div>
          <v-row
            class="mr-0"
          >
            <v-col :cols="isSuggestionsDisplayed ? 12 - suggestionCols : 12">
              <v-form
                ref="form"
                v-model="isValidTranslation"
              >

                <fieldset class="rounded-8 mx-4" v-if="!disableField('base') || settingData.template.base[chosenTranslation]">
                  <legend class="ml-4 px-2">List</legend>
                  <v-text-field
                    v-model="settingData.template.base[chosenTranslation].header"
                    :disabled="disableField('base')"
                    class="ma-4"
                    :error="!disableField('base') && !settingData.template.base[chosenTranslation].header"
                    :rules="!disableField('base') ? [v => !!v || 'Header is required', () => !disableField('base')] : []"
                    label="Header"
                  />
                  <v-textarea
                    v-model="settingData.template.base[chosenTranslation].content"
                    :disabled="disableField('base')"
                    auto-grow
                    :error="!disableField('base') && !settingData.template.base[chosenTranslation].content"
                    :rules="!disableField('base') ? [v => !!v || 'Content is required', () => !disableField('base')] : []"
                    class="mx-4 mt-2 pt-0"
                    label="Content"
                    rows="3"
                  />
                  <v-textarea
                    v-model="settingData.template.base[chosenTranslation].excerpt"
                    :disabled="disableField('base')"
                    auto-grow
                    :error="!disableField('base') && !settingData.template.base[chosenTranslation].excerpt"
                    :rules="!disableField('base') ? [v => !!v || 'Excerpt is required', () => !disableField('base')] : []"
                    class="mx-4 mt-2 pt-0"
                    label="Excerpt"
                    rows="3"
                  />
                </fieldset>


                <fieldset class="rounded-8 mx-4 mt-4" v-if="!disableField('toast') || settingData.template.toast[chosenTranslation]">
                  <legend class="ml-4 px-2">Toast</legend>
                  <v-textarea
                    v-model="settingData.template.toast[chosenTranslation].excerpt"
                    :disabled="disableField('toast')"
                    auto-grow
                    :error="!disableField('toast') && !settingData.template.toast[chosenTranslation].excerpt"
                    :rules="!disableField('toast') ? [v => !!v || 'Toast excerpt is required', () => !disableField('toast')] : []"
                    class="mx-4 mt-2"
                    label="Excerpt"
                    rows="3"
                  />
                </fieldset>

                <fieldset class="rounded-8 mx-4 mt-4" v-if="!disableField('popup') || settingData.template.popup[chosenTranslation]">
                  <legend class="ml-4 px-2">Popup</legend>
                  <v-text-field
                    v-model="settingData.template.popup[chosenTranslation].header"
                    :disabled="disableField('popup')"
                    auto-grow
                    :error="!disableField('popup') && !settingData.template.popup[chosenTranslation].header"
                    :rules="!disableField('popup') ? [v => !!v || 'Popup header is required', () => !disableField('popup')] : []"
                    class="ma-4"
                    label="Header"
                  />
                  <v-textarea
                    v-model="settingData.template.popup[chosenTranslation].content"
                    :disabled="disableField('popup')"
                    auto-grow
                    :error="!disableField('popup') && !settingData.template.popup[chosenTranslation].content"
                    :rules="!disableField('popup') ? [v => !!v || 'Popup content is required', () => !disableField('popup')] : []"
                    class="mx-4 mt-2 pt-0"
                    label="Content"
                    rows="3"
                  />
                  <v-textarea
                    v-model="settingData.template.popup[chosenTranslation].excerpt"
                    :disabled="disableField('popup')"
                    auto-grow
                    :error="!disableField('popup') && !settingData.template.popup[chosenTranslation].excerpt"
                    :rules="!disableField('popup') ? [v => !!v || 'Popup excerpt is required', () => !disableField('popup')] : []"
                    class="mx-4 mt-2 pt-0"
                    label="Excerpt"
                    rows="3"
                  />
                </fieldset>

                <fieldset class="rounded-8 mx-4 mt-4">
                  <legend class="ml-4 px-2">URL</legend>
                  <v-row class="mx-0 pl-4 mt-1 mb-3 mr-4">
                    <v-text-field
                      v-model="settingData.template.url[chosenTranslation].header"
                      hide-details="auto"
                      class="col-4"
                      label="Title"
                    ></v-text-field>
                    <v-text-field
                      v-model="settingData.template.url[chosenTranslation].content"
                      hide-details="auto"
                      class="col-8"
                      label="Link"
                    ></v-text-field>
                  </v-row>
                </fieldset>
              </v-form>
            </v-col>
            <v-col
              v-if="currentTriggerVars.length && isSuggestionsDisplayed"
              :cols="suggestionCols"
              style="position: relative;"
            >
              <v-card class="rounded-8 mt-3" style="position: sticky; top: 150px;">
                <v-list >
                  <v-subheader style="height: 25px">
                    Dynamic variable
                    <v-btn
                      small
                      icon
                      class="ml-auto"
                      color="error"
                      @click="isSuggestionsDisplayed = false"
                    >
                      <v-icon>
                        mdi-close
                      </v-icon>
                    </v-btn>
                  </v-subheader>
                  <v-list-item>
                    <v-btn-toggle v-model="suggestionCols">
                      <v-btn
                        icon
                        small
                        :value="2"
                      >
                        <v-icon>
                          mdi-tally-mark-2
                        </v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        small
                        :value="3"
                      >
                        <v-icon>
                          mdi-tally-mark-3
                        </v-icon>
                      </v-btn>
                    </v-btn-toggle>

                  </v-list-item>
                  <v-tooltip
                    v-for="variable in currentTriggerVars"
                    :key="variable.name"
                    left
                  >
                    <template #activator="{on, attrs}">
                      <v-list-item
                        v-on="on"
                        v-bind="attrs"
                        style="min-height: 36px;"
                      >
                        <v-list-item-content class="py-0">
                          <v-list-item-title v-text="variable.name" />
                        </v-list-item-content>
                        <v-list-item-action class="my-0">
                          <v-btn
                            icon
                            color="primary"
                            @click="copyText(`{{${variable.name}}}`)"
                          >
                            <v-icon>
                              mdi-content-copy
                            </v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </template>
                    <span v-text="variable.description" />
                  </v-tooltip>
                </v-list>

              </v-card>
            </v-col>
          </v-row>
        </v-card>

      </div>
    </div>
  </div>
</template>

<script>
  import DrawerDialogControls from '@/components/core/DrawerDialog/DrawerDialogControls.vue'
  import SelectLang from '@/components/core/SelectLang/SelectLang.vue'
  import LangChip from '@/components/core/SelectLang/LangChip.vue'
  import { localeToIsoPrefix, mapperIsoLangToOurLocal } from '@/components/core/SelectLang/mapperIsoLocale'
  import { TTL, AVAILABILITY_PERIODS, NOTIFICATION_TYPES, RECURRING_PERIODS, SEGMENT_FIELDS, SEGMENT_EQUATION, WEEKDAYS, RECURRING_UNITS } from './const'
  import { API } from '@/utils/api'
  import { mask } from 'vue-the-mask'
  import { cloneDeep, isObject, findIndex, find, forEach, xor } from 'lodash'
  // import { parse } from 'csv-parse'
  import { parseCSV } from '@/utils/helper'

  const SKELETON_NOTIFICATION = {
    name: '',
    trigger: '',
    languages: [ 'EN' ],
    rules: {
      filter: [
        // {
        //   field: '',
        //   value: '',
        //   equation: ''
        // }
      ],
      custom_filter: {},
      type: NOTIFICATION_TYPES[0].value,
      ttl: {
        type: 'day',
        amount: 5
      },
      global_ttl: {
        type: 'day',
        amount: 5
      },
      availability: {
        period: 'daily',
        start_time: '00:00:00',
        end_time: '23:59:59'
      },
      recurring: { period: 'none' },
      mail_template_id: null
    },
    template: {
      base: {
        EN: {
          header: '',
          content: '',
          excerpt: '',
        },
      },
      toast: {
        EN: {
          excerpt: '',
        },
      },
      popup: {
        EN: {
          header: '',
          content: '',
          excerpt: '',
        },
      },
      url: {
        EN: {
          header: '',
          content: '',
        },
      }
    }
  }
  export default {
    props: {
      isLoading: {
        type: Boolean,
        default: false
      },
      triggers: {
        type: Array,
        require: true,
      },
      readonly: {
        type: Boolean,
        default: false
      },
      types: {
        type: Array,
        require: true
      },
      entityId: {
        type: Number,
        require: true
      },
      notification: {
        type: Object,
        require: true
      },
      hasMailTemplateAccess: Boolean,
      mailTemplates: {
        type: Array,
        default: () => ([])
      },
      hasBonusToolsAccess: Boolean,
      benefitOptions: {
        type: Array,
        default: () => ([])
      },
    },

    directives: {
      mask,
    },

    components: {
      LangChip,
      DrawerDialogControls,
      SelectLang
    },
    data () {
      return {
        NOTIFICATION_TYPES,
        SEGMENT_EQUATION,
        AVAILABILITY_PERIODS,
        RECURRING_PERIODS,
        SEGMENT_FIELDS,
        WEEKDAYS,
        RECURRING_UNITS,
        TTL,
        chosenTranslation: 'EN',
        search: null,
        isOpen: false,
        isValidTranslation: false,
        settingData: cloneDeep(SKELETON_NOTIFICATION),
        selectedNotificationSet: [],
        keyExceptionCondition: 'account.id',
        translationKeys: [ 'base', 'toast', 'popup', 'url' ],
        isSuggestionsDisplayed: false,
        suggestionCols: 2,
      }
    },
    computed: {
      disableField () {
        return (option) => !this.selectedNotificationSet.includes(option)
      },
      isDisabledForm () {
        return !this.selectedNotificationSet.length || !this.isValidTranslation
      },
      filteredConditions () {
        return this.settingData.rules.filter?.filter?.(({ field }) => field !== this.keyExceptionCondition)
      },
      accountConditionsIds () {
        let index = findIndex(this.settingData.rules.filter, { field: 'account.id' })
        if (index == -1) return []
        return this.settingData.rules.filter[index].value || []
      },

      currentTrigger() {
        return find(this.triggers, { value: this.settingData.trigger })
      },

      customFilterOptions() {
        return this.currentTrigger ? Object.keys(this.currentTrigger.custom_filter ?? {}) : []
      },

      unusedFilterOptions() {
        return xor(this.customFilterOptions, Object.keys(this.settingData.rules.custom_filter ?? {}))
      },

      currentTriggerVars() {
        return this.currentTrigger?.variables ?? []
      },

      hasCustomFilter() {
        return !!this.settingData.rules.custom_filter
      },
    },
    watch: {
      'settingData.rules.availability.period': function (period, old) {
        if (old) {
          this.settingData.rules.availability = { ...this.settingData.rules.availability, period }
        }
      },
      'settingData.rules.recurring.period': function (period, old) {
        if (old) {
          this.settingData.rules.recurring = { ...this.settingData.rules.recurring, period }
        }
      },
      'settingData.trigger': function (newTrigger, oldTrigger) {
        this.$delete(this.settingData?.rules, 'custom_filter')
        let trigger = find(this.triggers, { value: newTrigger })

        if (oldTrigger && trigger) {
          if (trigger.custom_filter) {
            this.$set(this.settingData.rules, 'custom_filter', {})
          }
        } else if (!oldTrigger && trigger && !!trigger.custom_filter) {
          this.$set(this.settingData.rules, 'custom_filter', this.notification?.rules?.custom_filter ?? {})
        }
      },
      selectedNotificationSet: function (newVal, prevVal) {
        const diffVal = prevVal.filter(key => !newVal.includes(key))
        for (let key of diffVal) {
          if (!this.settingData.template[key][this.chosenTranslation] || this.settingData.template[key][this.chosenTranslation] && this.disableField((key))) {
            forEach(this.settingData.template[key], (_, translation) => {
              this.$set(
                this.settingData.template[key],
                translation,
                cloneDeep(SKELETON_NOTIFICATION.template[key].EN)
              )
            })
          }
        }
      },
      notification: {
        handler () {
          this.settingData = this.notification ? this.getNewSkeleton(this.notification) : this.getNewSkeleton()
          this.setNotificationSelected(this.settingData)
        },
        deep: true,
        immediate: true
      }
    },
    methods: {
      mapperIsoLangToOurLocal,
      setNotificationSelected () {
        this.selectedNotificationSet = Object.entries(this.settingData.template)
          .filter(([ key, translations ]) => {
            return this.translationKeys.includes(key) &&
              translations[this.chosenTranslation]?.header ||
              translations[this.chosenTranslation]?.excerpt ||
              translations[this.chosenTranslation]?.content
          })
          .map(([ key ]) => key)
      },
      getNewSkeleton (notification) {
        const template = notification || SKELETON_NOTIFICATION

        const presetTranslations = notification ? Object.fromEntries(
          this.translationKeys.map((key) => {
            if (!template.template[key]) {
              const baseLangTemplate = template.languages.map((lang) => [ lang, { ...SKELETON_NOTIFICATION.template[key]['EN'] } ])
              return [ key, Object.fromEntries(baseLangTemplate) ]
            }
            return [ key, template.template[key] ]
          })) : {}

        const checkAllTranslation = (templates, languages) => {
          this.translationKeys.map((key) => {
            languages.map((lang) => {
              templates[key][lang] = templates[key][lang] || cloneDeep(SKELETON_NOTIFICATION.template[key]['EN'])
            })
          })

          return templates
        }

        return cloneDeep({
          ...template,
          template: checkAllTranslation({ ...template.template, ...presetTranslations }, template.languages),
          trigger: template?.trigger || this.triggers[0].value,
          rules: {
            ...(template?.rules ?? {}),
            filter: [ ...(template?.rules?.filter ?? []), {
              field: '',
              value: '',
              equation: ''
            } ]
          }
        })
      },
      onChangeChosenTranslation (translation) {
        if (translation) {
          this.chosenTranslation = translation
        }
      },
      async addNewNotification () {
        this.$loader.loaded = false
        try {
          let event = { entity_id: this.entityId, ...cloneDeep(this.settingData) }
          event.rules.filter = event.rules.filter?.filter(({ field, value }) => field && value) || []

          const res = await API.call('/$v/notification-center/events/update.json', { event })
          this.$emit('add', res)
          this.settingData = this.getNewSkeleton()

          this.$showSuccess(`Add a new notification is successful`)
          this.$emit('close')
        } catch (e) {
        } finally {
          this.$loader.loaded = true
        }
      },

      clearAccountIdValues () {
        this.settingData.rules.filter = this.settingData.rules.filter.filter(rule =>
          rule.field !== this.keyExceptionCondition
        )
      },

      addUserId (id) {
        if (!id) {
          this.clearAccountIdValues()
          return
        }

        let index = findIndex(this.settingData.rules.filter, { field: 'account.id' })

        if (id.includes(',')) {
          const ids = id.split(',').map(value => value.trim()).filter(Boolean)

          if (ids.length) {
            for (const id of ids) {
              this.addUserId(id)
            }

            if (index !== -1) {
              // trigger reactivity to ensure up-to-date data is displayed in case none of the ids are pushed
              this.settingData.rules.filter[index].value.push(this.settingData.rules.filter[index].value.pop())
            }

            return
          }
        }

        if (index === -1) {
          this.settingData.rules.filter.push({
            field: 'account.id',
            value: [ id ],
            equation: 'in'
          })
        } else {
          if (!this.settingData.rules.filter[index].value.includes(id)) {
            this.settingData.rules.filter[index].value.push(id)
          }
        }
      },
      deleteRule (rule) {
        this.settingData.rules.filter = this.settingData.rules.filter.filter((condition) =>
          condition !== rule)
      },
      addRule () {
        this.settingData.rules.filter = [ ...this.settingData.rules.filter, {
          field: '',
          value: '',
          equation: ''
        } ]
      },
      localeToIsoPrefix,
      chooseLangForEdit (id) {
        const lang = this.settingData.languages[id]
        if (lang !== this.chosenTranslation) {
          this.chosenTranslation = lang
        }
      },
      addTranslationContent ([ locale ]) {
        for (const key of this.translationKeys) {
          this.$set(this.settingData.template[key], locale, { ...SKELETON_NOTIFICATION.template[key]['EN'] } || {
            header: '',
            content: '',
            excerpt: '',
          })
        }
        this.chosenTranslation = locale
      },
      deleteTranslationContent ([ locale, index ]) {
        const [ lang ] = locale

        if (lang === 'EN' || this.settingData.languages.length === 0) {
          this.settingData.languages.unshift('EN')
          return
        }
        if (this.chosenTranslation === lang) {
          this.chosenTranslation = this.settingData.languages.slice(-1)[0]
        }

        for (const key in this.settingData.template) {
          if (isObject(this.settingData.template[key])) {
            delete this.settingData.template[key][lang]
          }
        }
      },
      toggleLanguages (open) {
        this.isOpen = open
      },
      remove (item) {
        let index = findIndex(this.settingData.rules.filter, { field: 'account.id' })
        this.settingData.rules.filter[index].value = this.settingData.rules.filter[index].value.filter(id => id !== item)

        if (!this.settingData.rules.filter[index].value.length) this.clearAccountIdValues()
      },

      /**
       *
       * @param {Event & {target: HTMLInputElement}} event
       */
      onImportChange (event) {
        if (!event.target.files.length) {
          return
        }

        const file = event.target.files[0]

        const reader = new FileReader()
        reader.readAsText(file, 'utf-8')
        reader.onload = (event) => {
          try {

            let records = parseCSV(event.target.result)

            if (!('account_id' in records[0])) {

              this.$showError('Could not find column accound_id in results')

            } else {

              for (const record of records) {
                this.addUserId(record.account_id)
              }

              this.$showSuccess('Successfully imported columns')
            }

          } catch (err) {
            this.$showError(`Failed to parse CSV, err: ${err}`)
          }
        }
      },

      onAddCustomFilter(filterKey) {
        this.$set(this.settingData.rules.custom_filter, filterKey, cloneDeep(this.currentTrigger.custom_filter[filterKey]))
      },

      copyText(text) {
        return navigator.clipboard.writeText(text).then(() => {
          this.$showSuccess('Variable copied to clipboard')
        }).catch(err => {
          console.error('Error copying variable:', err)
          this.$showError('Error copying variable')
        })
      }
    }
  }
</script>

<style lang="scss" scoped>
  .setting-form {
    color: rgba(0, 0, 0, 87%);
    border-radius: 0 !important;

    fieldset {
      border: thin solid rgba(0, 0, 0, 0.35);
    }

    &-header {
      z-index: 300;
      position: sticky;
      top: 0;
      left: 0;
    }

    &-content {
      background-color: #F2F2F2;
      padding: 1rem;

      &__body {
        &-icon {
          position: absolute;
          top: 16px;
          right: 16px;
        }

        &-filter {
          border: thin solid rgba(0, 0, 0, .12);
          box-shadow: none !important;

          .filter-size {
            width: 90%;
            position: relative;
          }

          &-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin: 16px;
            width: 90%;

            & span {
              font-size: 18px;
            }
          }

          &-options {
            width: 100%;
            gap: 40px;
            justify-content: space-around;

            & > div {
              margin: 0 !important;
            }
          }
        }

        &-date {
          gap: 40px;
        }

        &-set {
          padding: 0px 16px;

          &-title {
            font-size: 18px;
          }

          &-options {
            display: flex;
            gap: 60px;
            align-items: center;

            &-select {
              padding: 0;
              margin: 0;
            }
          }
        }

        &-info {
          display: flex;
          gap: 15px;

          &-times {
            & ::v-deep .v-input__slot::before {
              border-image: none !important;
            }
          }
        }

        &-card {
          border-left: 3px solid #4EAF50;
        }

        &-wrapper {
          width: 80%;
        }

        &-localization {
          border: thin solid rgba(0, 0, 0, .12);
          box-shadow: none !important;

          &-choice {
            gap: 12px;

            &--active {
              background-color: #219653 !important;
            }
          }

          &-content {
            gap: 12px
          }

          &-text {
            font-size: 14px;
          }

          &-select-lang {
            z-index: 1;

            &-btn {
              display: flex;
              justify-content: center;
            }
          }
        }

        &-header {
          width: 546px;
        }

        &-content {
          display: flex;
          align-items: center;
          border: thin solid rgba(0, 0, 0, .42);
          box-shadow: none !important;
          border-radius: 4px;
          height: 82px;
          width: auto;

          & ::v-deep .v-input__control {
            display: flex;
            align-items: center;
          }

          & ::v-deep .v-input__slot::before {
            border: none !important;
          }

          & ::v-deep .v-input__slot::after {
            border: none !important;
          }

        }

        &-url {
          width: 80%;
          display: flex;
          gap: 32px;

          &-text {
            display: flex;
            flex: 0 220px;
          }

          &-link {
            display: flex;
            flex: 1 auto;
          }
        }

        &-title {
          font-size: 18px;
          display: flex;
          align-items: center;
        }

        &-btn {
          color: white;
          font-size: 13px;
          height: 32px !important;
          padding: 0 !important;

          & ::v-deep .v-btn__content {
            padding: 9px 10px;
          }
        }
      }
    }
  }
</style>
